import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import Button from 'src/components/Button';
import Toast from 'src/components/Toast';
import useAuth from 'src/hooks/useAuth';
import useUserPreferences from 'src/hooks/useUserPreferences';
import enRoles from 'src/models/enums/enRoles';
import { IPaginationParams, IPaginationResponse } from 'src/models/interfaces/pagination';
import { enStatusCode, IReservation } from 'src/models/interfaces/reservation';
import { IUserPreferences } from 'src/models/interfaces/userPreferences';
import reservationService from 'src/service/reservation';
import UserPreferences from '../UserPreferences';
import GridItem from './gridItem';
import TablePagination from './pagination';
import { GridContainer, Header, MasterCheckbox, MessageTD, Table, TBody, TFoot, THead } from './styles';

interface IProps {
  data: IPaginationResponse<IReservation[]>;
  error: boolean,
  loading: boolean;
  params: IPaginationParams,
  checkbox?: boolean;
  onCancel: (ids: number[]) => void;
  mergeParams: (newParams: Partial<IPaginationParams>) => void;
}

const DataGrid = ({
  data,
  error,
  loading,
  params,
  checkbox = true,
  onCancel,
  mergeParams
}: IProps) => {
  const [checkedItems, setCheckedItems] = useState([] as any[]);
  const [allChecked, setAllChecked] = useState(false);
  const [preferencesOpened, setPreferencesOpened] = useState(false);
  const toast = Toast();
  const prefKey = 'reservationReport';

  const { getCols } = useUserPreferences();
  const { total, results } = data || ({ total: 0, results: [] } as typeof data);
  const { user } = useAuth();

  const notCanceled = useMemo((): IReservation[] => {
    return (data?.results as any)?.filter((r: any) => r.statusCode !== enStatusCode.canceled);
  }, [data]);

  const colsPref = useMemo(() => getCols(prefKey), [getCols]);

  useEffect(() => {
    if (loading) {
      setAllChecked(false);
      setCheckedItems([]);
    }
  }, [loading]);

  const handleCheckAll = useCallback(({ currentTarget: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
    setAllChecked(checked);
    setCheckedItems(checked ? notCanceled || [] : []);
  }, [notCanceled]);

  const handleCheck = useCallback((checked: boolean, item: any) => {
    const items = [...checkedItems];

    if (checked) items.push(item);
    else {
      let index = items.findIndex(i => i.id === item.id);
      items.splice(index, index > -1 ? 1 : 0);
    }

    setCheckedItems(items);
    setAllChecked(notCanceled.length === items.length);
  }, [checkedItems, notCanceled]);

  const IsChecked = useCallback((id: number) => {
    return checkedItems.find(i => i.id === id);
  }, [checkedItems]);

  const handleCancelClick = useCallback(() => {
    const ids = checkedItems.map(i => i.id);
    onCancel(ids);
  }, [checkedItems, onCancel]);

  const handleExport = useCallback(() => {
    reservationService.exportReport(params)
      .then(({ data }) => {
        const url = window.URL.createObjectURL(data);
        var a = document.createElement("a");
        a.href = url;
        a.download = 'Relatório de Reservas.xlsx';
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => toast.error());
  }, [params]);

  const handlePreferences = useCallback(() => {
    setPreferencesOpened(!preferencesOpened);
  }, [preferencesOpened]);

  return (
    <>
      <UserPreferences opened={preferencesOpened} onClose={handlePreferences} colsPref={colsPref} prefKey={prefKey} />
      <Header>
        {checkbox ? <MasterCheckbox checked={allChecked} onChange={handleCheckAll} /> : ''}
        <Button fill={false} onClick={handleCancelClick} disabled={checkedItems.length === 0}>Cancelar Selecionados</Button>
        <div className="right">
          <Button fill={false} onClick={handleExport}>Exportar dados</Button>
          <Button onClick={handlePreferences}>Personalizar</Button>
        </div>
      </Header>
      <GridContainer>
        <Table>
          <THead>
            <tr>
              {checkbox ? (<th></th>) : ''}
              {colsPref.length > 0 &&
                <>
                  {colsPref.includes('country') && <th>País</th>}
                  {colsPref.includes('state') && <th>Estado</th>}
                  {colsPref.includes('city') && <th>Cidade</th>}
                </>
              }
              <th>Nome do Funcionário</th>
              <th>Prédio</th>
              <th>Andar</th>
              <th>Setor</th>
              <th>Mesa</th>
              <th>Data</th>
              <th>Status</th>
              {user && [enRoles.sysAdmin, enRoles.doctor].includes(user.roles) &&
                <th>PEM</th>}
            </tr>
          </THead>
          <TBody>
            {error ? <tr><MessageTD>Houve um erro ao obter os registros.</MessageTD></tr> :
              loading ? <tr><MessageTD>Carregando...</MessageTD></tr> :
                results.length > 0 ?
                  results.map((d: any) => <GridItem key={d.id} checkbox={checkbox} isChecked={IsChecked} item={d} onClick={handleCheck} colsPref={colsPref} />) :
                  <tr><MessageTD>Nenhum registro encontrado.</MessageTD></tr>
            }
          </TBody>
          <TFoot>
            <tr>
              <TablePagination colSpan={30} total={total} disabled={loading} paginationParams={params} onChange={mergeParams} />
            </tr>
          </TFoot>
        </Table>
      </GridContainer>
    </>
  );
};

export default DataGrid;